import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "@/app/types";
import { fetchQuizzes } from "@/api/fetchQuizzes/fetchQuizzesApi";
import { createQuiz } from "@/api/createQuiz/createQuizApi";
import { updateQuiz } from "@/api/updateQuiz/updateQuizApi";
import { deleteQuiz } from "@/api/deleteQuiz/deleteQuizApi";
import type {
  FetchQuizzesParams,
  FetchQuizzesResponse,
} from "@/api/fetchQuizzes/types";
import type {
  CreateQuizPayload,
  CreateQuizResponse,
} from "@/api/createQuiz/types";
import type {
  UpdateQuizPayload,
  UpdateQuizResponse,
} from "@/api/updateQuiz/types";
import type { QuizWithQuestions } from "@/modules/learningCenter/types/quiz";

/**
 * Quiz Management State
 *
 * Manages the list of quizzes with CRUD operations
 */
export interface QuizManagementState {
  quizzes: QuizWithQuestions[];
  totalCount: number;
  loading: boolean;
  error: string | null;
}

// Initial state
const initialState: QuizManagementState = {
  quizzes: [],
  totalCount: 0,
  loading: false,
  error: null,
};

/**
 * Async thunk: Fetch quizzes
 */
export const fetchQuizzesThunk = createAsyncThunk<
  FetchQuizzesResponse,
  FetchQuizzesParams | void,
  { rejectValue: string }
>("quizManagement/fetchQuizzes", async (params, { rejectWithValue }) => {
  try {
    const response = await fetchQuizzes(params || undefined);
    return response;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to fetch quizzes",
    );
  }
});

/**
 * Async thunk: Create a new quiz
 */
export const createQuizThunk = createAsyncThunk<
  CreateQuizResponse,
  CreateQuizPayload,
  { rejectValue: string }
>("quizManagement/createQuiz", async (payload, { rejectWithValue }) => {
  try {
    const response = await createQuiz(payload);
    return response;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to create quiz",
    );
  }
});

/**
 * Async thunk: Update an existing quiz
 */
export const updateQuizThunk = createAsyncThunk<
  UpdateQuizResponse,
  { id: number; payload: UpdateQuizPayload },
  { rejectValue: string }
>("quizManagement/updateQuiz", async ({ id, payload }, { rejectWithValue }) => {
  try {
    const response = await updateQuiz(id, payload);
    return response;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to update quiz",
    );
  }
});

/**
 * Async thunk: Delete a quiz
 */
export const deleteQuizThunk = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>("quizManagement/deleteQuiz", async (id, { rejectWithValue }) => {
  try {
    await deleteQuiz(id);
    return id;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to delete quiz",
    );
  }
});

/**
 * Quiz Management Slice
 */
const quizManagementSlice = createSlice({
  name: "quizManagement",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch quizzes
    builder
      .addCase(fetchQuizzesThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchQuizzesThunk.fulfilled, (state, action) => {
        state.loading = false;
        // Backend returns { data: [...quizzes], totalCount, page, pageSize }
        state.quizzes = action.payload.data;
        state.totalCount = action.payload.totalCount;
      })
      .addCase(fetchQuizzesThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch quizzes";
      });

    // Create quiz
    builder
      .addCase(createQuizThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createQuizThunk.fulfilled, (state, action) => {
        state.loading = false;
        // Add the new quiz to the beginning of the list
        // Backend returns quiz directly, not wrapped in data
        state.quizzes.unshift(action.payload);
        state.totalCount += 1;
      })
      .addCase(createQuizThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to create quiz";
      });

    // Update quiz
    builder
      .addCase(updateQuizThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateQuizThunk.fulfilled, (state, action) => {
        state.loading = false;
        // Backend returns quiz directly, not wrapped in data
        const index = state.quizzes.findIndex(
          (quiz) => quiz.id === action.payload.id,
        );
        if (index !== -1) {
          state.quizzes[index] = action.payload;
        }
      })
      .addCase(updateQuizThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to update quiz";
      });

    // Delete quiz
    builder
      .addCase(deleteQuizThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteQuizThunk.fulfilled, (state, action) => {
        state.loading = false;
        // Remove the quiz from the list
        state.quizzes = state.quizzes.filter(
          (quiz) => quiz.id !== action.payload,
        );
        state.totalCount -= 1;
      })
      .addCase(deleteQuizThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to delete quiz";
      });
  },
});

// Actions
export const { clearError } = quizManagementSlice.actions;

// Selectors
export const selectQuizzes = (state: RootState) =>
  state.quizManagement?.quizzes || [];
export const selectTotalCount = (state: RootState) =>
  state.quizManagement?.totalCount || 0;
export const selectLoading = (state: RootState) =>
  state.quizManagement?.loading || false;
export const selectError = (state: RootState) =>
  state.quizManagement?.error || null;

// Reducer
export default quizManagementSlice.reducer;
